import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import Card from '../../atoms/Card';
import LabelledColumn from '../../atoms/LabelledColumn';
import StatusBadge from '../../atoms/StatusBadge';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import BinSetLoadingSVG from '../../atoms/BinSetLoadingSVG';
import FeedFloToolTip from '../../atoms/FeedFloToolTip';
import { RightChevronIcon, InfoIcon } from '../../atoms/Icons';
import MultiBinVisual from '../../molecules/MultiBinVisual/MultiBinVisual';

import { convertGramsToLargeUnits } from '../../utils/unitConversion';
import { DATE_FORMAT_DASH, DATE_FORMAT_WEEKDAY_SHORT_MONTH_DAY, DATE_TIME_FORMAT_FULL } from '../../utils/dates';
import { censorTypes, useCensor } from '../../utils/hooks/useCensor/useCensor';
import { BinState, BinStateColour, OrderState, OrderStateString } from '../../utils/binSetConstants';
import { calcBinState, calcEmptyIn, splitBinSetLevelGramsByBinCapacities } from '../../utils/binSetUtils';

import './BinSetStatusCard.scss';
import { Link } from 'react-router-dom';
import withStaffOnlyIdWrapper from '../../utils/withStaffOnlyIdWrapper';
import BinControllerSection from '../../molecules/BinControllerSection';

/** True when `per_bin_level_in_grams` is present and safe to drive **Per Bin** UI. */
function hasValidPerBinLevels(map) {
  if (map == null || typeof map !== 'object' || Array.isArray(map)) return false;
  return Object.values(map).some((v) => typeof v === 'number' && Number.isFinite(v));
}

function BinSetStatusCard({
  className = '',
  loading = false,
  bins = [],
  binSetLevelInGrams = null,
  runout_prevention_bin_set_level_in_grams,
  earliest_delivery_bin_set_level_in_grams,
  emptyAt = 0,
  isMetric = false,
  order,
  binSetId = '',
  lastUpdatedAt = 0,
  hideInventoryLevels = false,
  binController = null,
  activeLockdown = null,
  /** From prediction `per_bin_level_in_grams`. When valid and there are 2+ bins, Remaining lists each bin. */
  perBinLevelInGrams = null,
}) {
  const { censor } = useCensor();

  const displayBinSetLevelGrams = binSetLevelInGrams;

  // Per-bin Remaining rows only when 2+ bins and `per_bin_level_in_grams` has at least one finite value.
  const { binsWithGrams, perBinRemainingRows } = useMemo(() => {
    const validMap = hasValidPerBinLevels(perBinLevelInGrams);
    const splitGrams =
      !hideInventoryLevels && !validMap
        ? splitBinSetLevelGramsByBinCapacities(bins, displayBinSetLevelGrams)
        : null;

    const withGrams = bins.map((bin, i) => {
      let levelGrams = null;
      const raw = bin.id != null ? perBinLevelInGrams?.[bin.id] : undefined;
      levelGrams = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
      if (levelGrams == null && splitGrams) {
        levelGrams = splitGrams[i];
      }
      return { ...bin, levelGrams };
    });

    const showPerBinRemaining = bins.length > 1 && validMap;
    const remainingRows = showPerBinRemaining
      ? withGrams.map((bin) => ({
        key: bin.id || String(bin.name),
        name: censor(bin.name || '?', censorTypes.bin),
        levelStr:
          bin.levelGrams != null ? convertGramsToLargeUnits(isMetric, bin.levelGrams, 1) : '—',
        capStr: convertGramsToLargeUnits(isMetric, bin.capacityInGrams || 0, 0),
      }))
      : null;

    return { binsWithGrams: withGrams, perBinRemainingRows: remainingRows };
  }, [bins, displayBinSetLevelGrams, hideInventoryLevels, perBinLevelInGrams, isMetric, censor]);

  if (loading) {
    return (
      <Card
        className={`BinSetStatusCard ${className}`}
        contentClassName="BinSetStatusCard-cardContentWrapper"
        status="none"
      >
        <BinSetLoadingSVG />
        <LoadingSkeleton className="BinSetStatusCard-title BinSetStatusCard-loadingShort" />
        <LoadingSkeleton className="MultiBinVisual-binSetIcon BinSetStatusCard-loadingIcon" />
        <LabelledColumn
          className="BinSetStatusCard-feedLineNames BinSetStatusCard-loadingMid"
          heading="Feed Lines"
          loading
        />
        <LabelledColumn className="BinSetStatusCard-capacity BinSetStatusCard-loadingMid" heading="Remaining" loading />
        <LabelledColumn className="BinSetStatusCard-emptyIn BinSetStatusCard-loadingMid" heading="Empty In" loading />
        <LabelledColumn
          className="BinSetStatusCard-lastDelivery BinSetStatusCard-loadingMid"
          heading="Last Delivery"
          loading
        />
      </Card>
    );
  }

  const binNames = bins.map(({ name }) => name || '?');
  const name = `Bin${binNames.length > 1 ? 's' : ''} ${binNames.join(', ')}`;

  const capacityInGrams = bins.reduce((sum, { capacityInGrams = 0 }) => sum + capacityInGrams, 0);
  const capacityInLargeUnits = convertGramsToLargeUnits(isMetric, capacityInGrams, 0);
  const binSetLevelInLargeUnits =
    displayBinSetLevelGrams != null ? convertGramsToLargeUnits(isMetric, displayBinSetLevelGrams, 1) : '?';

  const { emptyInHeading, emptyInString } = calcEmptyIn(emptyAt, displayBinSetLevelGrams);

  const lastDelivery = bins.reduce((lastDelivery, bin) => Math.max(lastDelivery, bin.lastDelivery || 0), 0);

  let orderTime = order?.delivered_at || order?.ordered_at;

  const binState = calcBinState(
    displayBinSetLevelGrams,
    earliest_delivery_bin_set_level_in_grams,
    runout_prevention_bin_set_level_in_grams,
  );
  const binStateErrorHighlight =
    !hideInventoryLevels &&
    BinState.ERROR === binState &&
    OrderState.SCHEDULED !== order?.status &&
    OrderState.SHIPPED !== order?.status;

  const binSetName = censor(name, censorTypes.bin);
  const IdWrappedTitle = withStaffOnlyIdWrapper(binSetName);
  return (
    <div className="BinSetStatusCard">
      <Card
        className={`BinSetStatusCard-card ${binStateErrorHighlight ? 'BinSetStatusCard-card--error' : ''} ${order?.status ? 'BinSetStatusCard-card--order' : ''
          } ${className}`}
        contentClassName="BinSetStatusCard-cardContentWrapper"
        status={hideInventoryLevels ? 'none' : BinState.ERROR === binState ? 'error' : 'none'}
        accentLocation="top"
      >
        <div className="BinSetStatusCard-titlerow">
          <span className="BinSetStatusCard-title BinSetStatusCard-titleText">
            <IdWrappedTitle data={binSetId} hoverText="Copy Bin Set ID" />
          </span>
          {hideInventoryLevels && (
            <FeedFloToolTip
              description="Bin Inventory tracking is disabled. Enable it in Barn Settings to view bin levels and predictions."
              position="top"
            >
              <InfoIcon className="BinSetStatusCard-infoIcon" />
            </FeedFloToolTip>
          )}
        </div>
        <MultiBinVisual
          bins={binsWithGrams}
          iconFillColour={hideInventoryLevels ? '#cccccc' : BinStateColour[binState]}
          inventoryHidden={hideInventoryLevels}
        />
        {!hideInventoryLevels && (
          <LabelledColumn className="BinSetStatusCard-capacity" heading="Remaining">
            {perBinRemainingRows ? (
              <ul className="BinSetStatusCard-perBinLevels">
                {perBinRemainingRows.map(({ key, name: binRowName, levelStr, capStr }) => (
                  <li key={key} className="BinSetStatusCard-perBinLevelItem">
                    <span className="BinSetStatusCard-perBinLevelName">{binRowName}:</span>
                    <span className="BinSetStatusCard-perBinLevelValue">{levelStr}</span>
                    <span className="BinSetStatusCard-capacityText">{` / ${capStr} ${isMetric ? 'Tonnes' : 'Tons'
                      }`}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <>
                <span className="BinSetStatusCard-capacityStrongText">{binSetLevelInLargeUnits}</span>
                <span className="BinSetStatusCard-capacityText">{` / ${capacityInLargeUnits} ${isMetric ? 'Tonnes' : 'Tons'
                  }`}</span>
              </>
            )}
          </LabelledColumn>
        )}
        {!hideInventoryLevels && (
          <LabelledColumn className="BinSetStatusCard-emptyIn" heading={emptyInHeading}>
            <span className="BinSetStatusCard-contentText">
              {binStateErrorHighlight ? (
                <StatusBadge text={emptyInString.toUpperCase()} status="error" />
              ) : (
                emptyInString
              )}
            </span>
          </LabelledColumn>
        )}
        {lastDelivery ? (
          <LabelledColumn className="BinSetStatusCard-lastDelivery" heading="Last Delivery">
            <span className="BinSetStatusCard-contentText">
              {dayjs.tz(1000 * lastDelivery).format(DATE_FORMAT_DASH)}
            </span>
          </LabelledColumn>
        ) : (
          <></>
        )}
        {binController && (
          <BinControllerSection
            className="BinSetStatusCard-binController"
            deviceId={binController.device_id}
            currentBinIndex={binController.state?.current_bin_index}
            targetBinIndex={binController.state?.target_bin_index}
            binControllerBins={binController.bin_controller_bins || []}
            bins={bins}
            binSetId={binSetId}
            isMetric={isMetric}
            isOnline={binController.device?.active_device_unreachable_fault?.length === 0}
            activeLockdown={activeLockdown}
            deviceHwVersion={binController.device?.hw_version}
          />
        )}
        {!hideInventoryLevels && lastUpdatedAt > 0 && (
          <div className="BinSetStatusCard-lastUpdated">
            <FeedFloToolTip
              description={`${dayjs.tz(lastUpdatedAt * 1000).format(DATE_TIME_FORMAT_FULL)}`}
              position="bottom"
            >
              <span>
                Last updated:
                <span> {dayjs.tz(lastUpdatedAt * 1000).fromNow()}</span>
              </span>
            </FeedFloToolTip>
          </div>
        )}
      </Card>
      {order && (
        <Link to={`/FeedDesk?trip=${order.trip_id}`}>
          <Card
            className={`BinSetStatusCard-order ${binStateErrorHighlight ? 'BinSetStatusCard-order--error' : ''}`}
            contentClassName="BinSetStatusCard-orderContent"
            accentLocation="left"
            status={order.status || 'none'}
          >
            {OrderStateString[order.status]} - {dayjs.unix(orderTime).format(DATE_FORMAT_WEEKDAY_SHORT_MONTH_DAY)}
            <RightChevronIcon className="BinSetStatusCard-chevronIcon" />
          </Card>
        </Link>
      )}
    </div>
  );
}

BinSetStatusCard.propTypes = {
  className: PropTypes.string,
  loading: PropTypes.bool,
  order: PropTypes.object,
  bins: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      capacityInGrams: PropTypes.number,
      name: PropTypes.string,
      lastDelivery: PropTypes.number,
      recommendedDelivery: PropTypes.number,
      scheduledDelivery: PropTypes.number,
      shippedDelivery: PropTypes.number,
    }),
  ),
  feedLineNames: PropTypes.arrayOf(PropTypes.string),
  binSetLevelInGrams: PropTypes.number,
  runout_prevention_bin_set_level_in_grams: PropTypes.number,
  earliest_delivery_bin_set_level_in_grams: PropTypes.number,
  emptyAt: PropTypes.number,
  isMetric: PropTypes.bool,
  binSetId: PropTypes.string,
  lastUpdatedAt: PropTypes.number,
  hideInventoryLevels: PropTypes.bool,
  binController: PropTypes.shape({
    device_id: PropTypes.string,
    state: PropTypes.shape({
      current_bin_index: PropTypes.number,
      target_bin_index: PropTypes.number,
    }),
    bin_controller_bins: PropTypes.arrayOf(
      PropTypes.shape({
        index: PropTypes.number,
        bin: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        }),
      }),
    ),
    device: PropTypes.shape({
      hw_version: PropTypes.string,
      active_device_unreachable_fault: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
        }),
      ),
    }),
  }),
  activeLockdown: PropTypes.oneOf(['hard', 'soft', 'intentional']),
  perBinLevelInGrams: PropTypes.object,
};

export default BinSetStatusCard;
